import { z } from 'zod';
import superjson from 'superjson';
import { Selectable } from 'kysely';
import { SessionThemes } from '../../helpers/schema';

export const schema = z.object({
  sessionId: z.string().optional(), // Optional - if not provided, returns global themes
  limit: z.coerce.number().min(1).max(100).optional().default(20),
  offset: z.coerce.number().min(0).optional().default(0),
  includePublic: z.coerce.boolean().optional().default(false), // Include public themes from other users
});

export type InputType = z.infer<typeof schema>;

export type ThemeOutput = Selectable<SessionThemes> & {
  pairedTheme?: Selectable<SessionThemes>; // Include paired theme info
  creatorName?: string; // For public themes
};

export type OutputType = {
  themes: ThemeOutput[];
  total: number; // Total count for pagination
  hasMore: boolean; // For infinite scroll
};

export const getThemesList = async (input: InputType, init?: RequestInit): Promise<OutputType> => {
  const params = new URLSearchParams();
  if (input.sessionId) params.append('sessionId', input.sessionId);
  if (input.limit) params.append('limit', input.limit.toString());
  if (input.offset) params.append('offset', input.offset.toString());
  if (input.includePublic) params.append('includePublic', 'true');

  const result = await fetch(`/_api/themes/list?${params.toString()}`, {
    method: 'GET',
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!result.ok) {
    const errorObject = superjson.parse<{ error: string }>(await result.text());
    throw new Error(errorObject.error);
  }
  return superjson.parse<OutputType>(await result.text());
};
